/**
 * Done. CPD — Landing Start CTA Section (Stitch v2 / screen 4)
 * Survey-entry block with two supporting USPs + trust badges.
 */

interface StartCTAProps {
  onStart: () => void;
}

const FEATURES = [
  {
    icon: 'auto_awesome',
    title: 'Precision Matching',
    desc: 'We match course materials to your actual daily chiropractic cases.',
  },
  {
    icon: 'schedule',
    title: 'Time Optimisation',
    desc: 'Focus only on what you need to meet your 20-hour requirement.',
  },
] as const;

const BADGES = [
  { icon: 'verified_user', label: 'NZCB Aligned' },
  { icon: 'security',      label: 'Privacy Protected' },
  { icon: 'psychology',    label: 'Evidence Based' },
] as const;

export function StartCTA({ onStart }: StartCTAProps): React.ReactElement {
  return (
    <section
      id="section-start"
      className="py-20 md:py-24 bg-[#f9f9f7]"
    >
      <div
        className="mx-auto px-4 md:px-10"
        style={{ maxWidth: 'var(--stitch-container-max)' }}
      >
        <div className="w-full max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
          {/* Copy */}
          <div className="md:col-span-7 flex flex-col gap-8 md:gap-10 order-2 md:order-1">
            <div className="flex flex-col gap-3">
              <span className="text-[var(--stitch-primary-container)] stitch-font-label text-[13px] tracking-[0.3em] uppercase opacity-80">
                Step One
              </span>
              <h2 className="stitch-font-display text-[40px] md:text-[56px] text-[var(--stitch-primary)] leading-[1.05] uppercase tracking-tight">
                Start Your Journey.
              </h2>
            </div>

            <p className="stitch-font-body text-[17px] text-[var(--stitch-on-surface-variant)] max-w-xl leading-relaxed">
              Our brief personalisation survey tailors your entire CPD year based on your specific
              practice focus, clinical interests, and certification requirements.
            </p>

            <div className="flex flex-col gap-6">
              {FEATURES.map(f => (
                <div key={f.title} className="flex items-start gap-5">
                  <div className="p-3 bg-[var(--stitch-surface-container-high)] rounded-full flex items-center justify-center">
                    <span className="material-symbols-outlined text-[var(--stitch-primary)]">{f.icon}</span>
                  </div>
                  <div>
                    <h3 className="stitch-font-headline text-[20px] text-[var(--stitch-on-surface)] mb-1">
                      {f.title}
                    </h3>
                    <p className="stitch-font-body text-[15px] text-[var(--stitch-on-surface-variant)] opacity-90">
                      {f.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-2">
              <button
                type="button"
                onClick={onStart}
                className="bg-[var(--stitch-primary)] text-[var(--stitch-on-primary)] px-8 md:px-10 py-4 md:py-5 rounded stitch-font-headline text-[20px] flex items-center gap-4 hover:bg-[var(--stitch-primary-container)] hover:-translate-y-0.5 transition-all stitch-ghost-shadow group uppercase tracking-wider"
              >
                Start
                <span className="material-symbols-outlined group-hover:translate-x-2 transition-transform">arrow_forward</span>
              </button>
              <p className="mt-4 stitch-font-label text-[13px] text-[var(--stitch-outline)] italic normal-case tracking-normal">
                Takes about 5 minutes to complete.
              </p>
            </div>
          </div>

          {/* Visual anchor */}
          <div className="md:col-span-5 order-1 md:order-2">
            <div className="relative w-full aspect-[4/5] rounded-xl overflow-hidden stitch-teal-border stitch-ghost-shadow">
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage:
                    "url('https://lh3.googleusercontent.com/aida-public/AB6AXuA73ZynyEwX7eidyBFHg9jzVhrKjmvxYeGDiVXpDFetJFNr90snaahw36NeN5l9tUaSJ3nmrC3GuctXE5IfJFfvcC67BXiWfdQI0jqp76P5WsxcOo5zJWRl9JzmAQAVLUhLPXhUnRXa18-JhrbNAHmnj6InCiSPzuXsAf0aeJJHI2K1cB2VeqeoOxpY0EqWhlgk4k6yg-8uLuoKoRk6IEb8_HmPmLERuhq6Aak38_gsGprLRuT9cuCwt_NCFkrdhppQvUD_npZwmKA')",
                }}
                aria-hidden="true"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--stitch-primary)]/30 to-transparent" />
              <div className="absolute bottom-6 left-5 right-5 bg-white/95 backdrop-blur-md p-6 rounded shadow-2xl border-t-2 border-[var(--stitch-primary)]">
                <div className="flex items-center justify-between mb-4">
                  <span className="stitch-font-label text-[12px] text-[var(--stitch-primary)] tracking-widest uppercase">Your Goal</span>
                  <span className="stitch-font-headline text-[20px] text-[var(--stitch-primary)]">20 Hours</span>
                </div>
                <div className="w-full h-1.5 bg-[var(--stitch-surface-container-highest)] rounded-full overflow-hidden">
                  <div className="h-full bg-[var(--stitch-primary)]" style={{ width: '15%' }} />
                </div>
                <p className="mt-3 stitch-font-body text-[14px] text-[var(--stitch-on-surface-variant)] italic">
                  Personalised roadmap ready for calculation.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Trust badges */}
        <div className="mt-16 md:mt-20 w-full flex flex-wrap justify-center gap-10 md:gap-16 border-t border-[var(--stitch-surface-container-highest)] pt-12">
          {BADGES.map(b => (
            <div key={b.label} className="flex items-center gap-3 text-[var(--stitch-on-surface-variant)]">
              <span className="material-symbols-outlined text-[20px]">{b.icon}</span>
              <span className="stitch-font-label text-[13px] uppercase tracking-wider">{b.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
